/**
 * Backend Health Check
 * 
 * Uses a fast initial probe (2s) for quick startup, with a slower
 * confirmation retry if the first attempt fails. This avoids making
 * users wait 8+ seconds on every app launch while still preventing
 * false-negative "down" results.
 */

const FAST_TIMEOUT_MS = 2500;
const SLOW_TIMEOUT_MS = 6000;
const RETRY_DELAY_MS = 1000;

async function probe(timeoutMs: number): Promise<boolean> {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;
  if (!url || !key) return false;

  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    const res = await fetch(`${url}/rest/v1/`, {
      method: 'HEAD',
      headers: {
        apikey: key,
        Authorization: `Bearer ${key}`,
      },
      signal: controller.signal,
    });
    clearTimeout(timer);

    // Any response (even 4xx) means server is alive
    return res.status < 500;
  } catch {
    return false;
  }
}

/**
 * Fast check: 2.5 s timeout → if fail, wait 1 s → retry with 6 s timeout.
 * Total worst-case: ~9.5 s (only on true outage).
 * Happy path: <2.5 s.
 */
export async function checkBackendHealth(): Promise<boolean> {
  // Fast probe
  if (await probe(FAST_TIMEOUT_MS)) return true;

  console.warn('[HealthCheck] Fast probe failed, retrying with longer timeout…');
  await new Promise(r => setTimeout(r, RETRY_DELAY_MS));

  // Slow confirmation
  return probe(SLOW_TIMEOUT_MS);
}
